import { useFormik } from "formik";
import * as Yup from "yup";
import { useMemo, useState } from "react";
import Button from "components/common/Button";
import { Button as ButtonAntd } from "antd";
import Constants from "helpers/constants";
import Colors from "assets/Colors";
import { BSON } from "realm-web";
import useCustomerMutations from "graphql/useCustomerMutations";
import moment from "moment";
import { useTranslation } from "react-i18next";
import FormHeader from "components/common/FormHeader";
import s from "./styles";
import useCustomers from "../../../graphql/useCustomers";
import { stringLPad } from "../../../helpers/utils";
import { useMessaging } from "../../../context/MessagingContext";
import GeneralBody from "./GeneralBody";

function createRef(customers) {
  let idx = 1;
  let currentRef = `C${stringLPad(`${idx}`, 5)}`;
  const refs = customers
    .filter((customer) => !!customer.ref)
    .map((customer) => customer.ref)
    .sort((ref1, ref2) => ref1.localeCompare(ref2));
  refs.forEach((ref) => {
    if (ref === currentRef) {
      currentRef = `C${stringLPad(`${++idx}`, 5)}`;
    }
  });
  return currentRef;
}

function AddCustomer({
  closeModal,
  customer,
  onUpdateSuccess,
  onAddSuccess,
  onDeleteRequest,
}) {
  const { t } = useTranslation();
  const { addCustomer, updateCustomer } = useCustomerMutations();

  const { setErrorMessage } = useMessaging();

  const { customers, loading: loadingCustomer } = useCustomers();
  const [dbLoading, setDbLoading] = useState(false);
  const loading = loadingCustomer || dbLoading;

  const suggestedRef = useMemo(() => createRef(customers), [customers]);

  const [section] = useState(Constants.GENERAL_SECTION);

  const {
    handleSubmit,
    getFieldProps,
    values,
    setFieldValue,
    setFieldTouched,
    errors,
    handleChange,
    touched,
    isValid,
    dirty,
  } = useFormik({
    initialValues: {
      _id: customer?._id || new BSON.ObjectId(),
      name: customer?.name || "",
      idNumber: customer?.idNumber || "",
      lastName: customer?.lastName || "",
      phone: customer?.phone || "",
      email: customer?.email || "",
      address1: customer?.address1 || "",
      address2: customer?.address2 || "",
      city: customer?.city || "",
      country: customer?.country || "",
      webPage: customer?.webPage || "",
      ref: customer?.ref || suggestedRef,
      tags: customer?.tags || ["person"],
      contactEmail: customer?.contactEmail,
      contactName: customer?.contactName,
      contactPhone: customer?.contactPhone,
      language: customer?.language,
      lastPurchaseDatetime: customer?.lastPurchaseDatetime,
      notes: customer?.notes,
      phone2: customer?.phone2,
      zip: customer?.zip || "",
      storeId: customer?.storeId,
      dateOfBirth: customer?.dateOfBirth && moment(customer.dateOfBirth),
      creationDatetime: customer?.creationDatetime || new Date(),
      updateDatetime: new Date(),
    },
    onSubmit: async (values) => {
      if (!customer) {
        // update user
        setDbLoading(true);
        addCustomer(values)
          .then(() => {
            setDbLoading(false);
            closeModal();
            onAddSuccess();
          })
          .catch((err) => {
            setDbLoading(false);
            setErrorMessage({ error: err });
          });
      } else {
        setDbLoading(true);
        updateCustomer(customer, values)
          .then(() => {
            closeModal();
            setDbLoading(false);
            onUpdateSuccess();
          })
          .catch((err) => {
            setDbLoading(false);
            setErrorMessage({ error: err });
          });
      }
    },
    validationSchema: Yup.object({
      name: Yup.string().required().max(255),
      lastName: Yup.string()
        .nullable()
        .when("tags", {
          is: (tags) => tags[0] === "person",
          then: Yup.string().required(),
        }),
      idNumber: Yup.string()
        .nullable()
        .when("tags", {
          is: (tags) => tags[0] === "business",
          then: Yup.string().required(),
        }),
      address1: Yup.string().when("tags", {
        is: (tags) => tags[0] === "business",
        then: Yup.string().required(),
      }),
      city: Yup.string().when("tags", {
        is: (tags) => tags[0] === "business",
        then: Yup.string().required(),
      }),
      country: Yup.string().when("tags", {
        is: (tags) => tags[0] === "business",
        then: Yup.string().required(),
      }),
      zip: Yup.string()
        .nullable()
        .when("tags", {
          is: (tags) => tags[0] === "business",
          then: Yup.string().required(),
        }),
      email: Yup.string().email(),
    }),
  });

  const saveButtonDisabled = !isValid || !dirty;

  return (
    <>
      <FormHeader
        addTitle={t("components.CustomerList.AddCustomer.Header.addCustomer")}
        updateTitle={t(
          "components.CustomerList.AddCustomer.Header.editCustomer"
        )}
        closeModal={closeModal}
        updating={!!customer}
        onSave={handleSubmit}
        saving={loading}
        enabled={isValid && dirty}
      />
      <s.BodyContainer>
        {section === Constants.GENERAL_SECTION && (
          <GeneralBody
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            getFieldProps={getFieldProps}
            setFieldTouched={setFieldTouched}
            values={values}
            touched={touched}
            errors={errors}
          />
        )}
        <s.BottomButtonsContainer>
          {!!customer && (
            <ButtonAntd
              disabled={loading}
              style={{ color: Colors.notification_failed }}
              type="text"
              onClick={() => {
                onDeleteRequest(customer);
              }}
            >
              {t("containers.CustomerList.AddCustomer.deleteCustomer")}
            </ButtonAntd>
          )}
          <Button
            disabled={saveButtonDisabled}
            loading={loading}
            type="primary"
            style={{
              background: saveButtonDisabled
                ? `${Colors.primary}30`
                : Colors.primary,
              color: Colors.text_on_secondary,
              width: "169px",
              fontWeight: "500",
              border: "none",
              borderRadius: "5px",
              height: "40px",
            }}
            onClick={handleSubmit}
          >
            {t("containers.CustomerList.AddCustomer.save")}
          </Button>
        </s.BottomButtonsContainer>
      </s.BodyContainer>
    </>
  );
}

export default AddCustomer;
